/**
 * A market's on-chain grid, read from DataStore.
 *
 * `getMarketContractSpecs` is the chain's own answer to "what sizes and prices
 * will this market accept": the order path already consults it (see
 * `floorPerpSizeToStep` in `hooks/api/order/usePerpOrder.helpers.ts`) so a size
 * can never revert `SizeNotOnStep` after the trader has signed. The FORM needs
 * the same four numbers, before a keystroke rather than after a signature, so
 * the field, the MAX button and the submit guard all sit on the grid the market
 * really has instead of the one the API advertises.
 *
 * The specs read here are cached for five minutes: a market's steps are a
 * registration-time property, not a tick.
 *
 * NOTE: the ABI fragment below is duplicated from `usePerpOrder.helpers.ts`
 * rather than shared. Extracting it would mean editing that file, and this phase
 * is additive only; the two should be folded together when the order path is
 * migrated.
 */
import { ethers } from "ethers";
import useSWR from "swr";
import { ChainConfig, getCurrentChainConfig } from "../../config/chains";
import { Dec, dec } from "../../utils/amount/decimal";
import { readProvider } from "../../utils/wallet/activeSigner";

/** Perp sizes and prices are 1e18 on every chain. */
const PERP_UNIT_DECIMALS = 18;

export const MARKET_CONTRACT_SPECS_ABI = [
  "function getMarketContractSpecs(address) view returns (uint256 priceStep, uint256 amountStep, uint256 minOrderSize, uint256 maxOrderSize)",
];

export interface MarketContractSpecs {
  priceStep: Dec;
  amountStep: Dec;
  minOrderSize: Dec;
  /** Absent when the market sets no cap — the chain writes that as 0. */
  maxOrderSize?: Dec;
}

const toDec = (value: unknown): Dec =>
  dec.fromUnits(String(value ?? "0"), PERP_UNIT_DECIMALS);

/**
 * Read the four specs for `market`. Throws rather than guessing: a grid the
 * chain did not confirm is worse than no grid, because the form would then
 * advertise sizes the market rejects.
 */
export const fetchMarketContractSpecs = async (
  config: ChainConfig,
  market: string
): Promise<MarketContractSpecs> => {
  const dataStore = config.contracts?.perpetualDex?.dataStore;
  if (!dataStore || dataStore === ethers.constants.AddressZero) {
    throw new Error(
      "DataStore address is not configured for this chain — cannot read the market's contract specs."
    );
  }

  // A view call over the chain's CONFIGURED RPC, never the wallet: the wallet
  // may sit on another network, where this address holds no contract.
  const contract = new ethers.Contract(
    dataStore,
    MARKET_CONTRACT_SPECS_ABI,
    readProvider(config)
  );
  const raw: any = await contract.getMarketContractSpecs(market);

  const specs: MarketContractSpecs = {
    priceStep: toDec(raw?.priceStep ?? raw?.[0]),
    amountStep: toDec(raw?.amountStep ?? raw?.[1]),
    minOrderSize: toDec(raw?.minOrderSize ?? raw?.[2]),
    // 0 from the chain means "no cap", not "nothing may be ordered".
    maxOrderSize: undefined,
  };

  if (!dec.gt(specs.amountStep, "0")) {
    throw new Error(
      "Market amountStep is zero — the market may not be registered on this chain."
    );
  }

  const maxOrderSize = toDec(raw?.maxOrderSize ?? raw?.[3]);
  if (dec.gt(maxOrderSize, "0")) {
    specs.maxOrderSize = maxOrderSize;
  }
  return specs;
};

export const useMarketContractSpecs = (market?: string) => {
  const config = getCurrentChainConfig();
  const { data, error, isLoading } = useSWR<MarketContractSpecs, Error>(
    market ? ["market-contract-specs", config.chainId, market] : null,
    () => fetchMarketContractSpecs(config, market as string),
    {
      // A market's grid is set at registration; re-reading it on every focus
      // would be one RPC round trip per tab switch for a value that never moves.
      dedupingInterval: 300_000,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  return { specs: data, error, isLoading };
};
